import { RadioGroup, Radio, Box } from '@everlywell/ui-kit';
import { PurchaseOptionsDrawerProduct } from 'components/PurchaseOptionsDrawerProvider/PurchaseOptionsDrawerProvider.context';
import { last, sortBy } from 'lodash-es';
import React, { useEffect, useMemo } from 'react';
import { VITAMINS } from 'utils/constants/products';
import { SubscriptionVariantType } from 'utils/types';

import PurchaseOption, {
  PurchaseOptionDescription,
  PurchaseOptionHeading,
  PurchaseOptionProps,
} from '../PurchaseOption/PurchaseOption';

export type SubscribeAndSaveOptionProps = Pick<
  PurchaseOptionProps,
  'id' | 'onSelected'
> & {
  product: PurchaseOptionsDrawerProduct | null | undefined;
  onSubscriptionVariantChange: (variant: SubscriptionVariantType) => void;
  isExpanded?: boolean;
};

/**
 * Renders the Subscribe and Save option
 */
function SubscribeAndSaveOption(
  props: SubscribeAndSaveOptionProps,
): JSX.Element | null {
  const { product, isExpanded, onSubscriptionVariantChange } = props;
  const [optionIndexString, setOptionIndexString] = React.useState('0');
  const sortedSubscriptionVariants = useMemo(
    () =>
      sortBy(
        product?.subscriptionVariants ?? [],
        (variant) => variant.subscription_plan?.percent_discount ?? 0,
      ),
    [product?.subscriptionVariants],
  );

  /**
   * Initialize and handle the subscription option change
   * When the subscription option is expanded, we need to select the proper variant based on the selected index
   */
  useEffect(() => {
    if (!isExpanded || !product) {
      return;
    }

    const index = parseInt(optionIndexString);

    const variant = sortedSubscriptionVariants[index];

    if (variant) {
      onSubscriptionVariantChange(variant);
    }
  }, [
    isExpanded,
    product,
    onSubscriptionVariantChange,
    optionIndexString,
    sortedSubscriptionVariants,
  ]);

  // If the product is not available or the subscription is hidden, we don't render the component
  if (
    !product ||
    // TODO: Remove this when hideSubscription is added to the new PDPs or Products in Contentful
    !VITAMINS.includes(product?.slug) ||
    product?.hideSubscription ||
    sortedSubscriptionVariants.length === 0
  ) {
    return null;
  }

  /**
   * Handles the change of the subscription option
   * @param index
   */
  const handleSubscriptionChange = (index: string) => {
    setOptionIndexString(index);
  };

  const variantWithMaxDiscount = last(sortedSubscriptionVariants);

  return (
    <PurchaseOption
      id={props.id}
      onSelected={props.onSelected}
      heading={<PurchaseOptionHeading>Subscribe & Save</PurchaseOptionHeading>}
      description={
        <PurchaseOptionDescription color="utility.sale">
          Save up to $
          {variantWithMaxDiscount?.subscription_plan?.percent_discount}%
        </PurchaseOptionDescription>
      }
      //NOTE: it assumes the variant with the max discount is the one billed monthly
      price={{
        amount: parseFloat(variantWithMaxDiscount?.price ?? '0'),
        unitLabel: '/month',
      }}
    >
      <RadioGroup
        name="subscriptionOption"
        value={optionIndexString}
        onChange={handleSubscriptionChange}
        display="flex"
        flexDirection="column"
        gap={5}
      >
        {sortedSubscriptionVariants.map((variant, index) => (
          <Radio key={variant.id} value={index.toString()}>
            <PurchaseOptionDescription>
              {variant.subscription_plan?.name ?? ''}{' '}
              <Box as="span" color="utility.sale">
                Save {variant.subscription_plan?.percent_discount || 0}%
              </Box>
            </PurchaseOptionDescription>
          </Radio>
        ))}
      </RadioGroup>
    </PurchaseOption>
  );
}

export default SubscribeAndSaveOption;
